/**
 * The speech-stop key (`shortcut.speech.stop`, Escape by default).
 *
 * While the key is armed, one press stops agent speech and does nothing else.
 * It is armed while speech is playing or queued, unless a one-shot dictation
 * is in progress: that owns its own cancel key (`shortcut.voice.cancel`).
 * Live talk does not disarm it, so stopping speech never ends live talk.
 *
 * The native input adapters claim the key system-wide while the
 * `set_tts_active` flag is set (see `shortcut_speech_stop.rs`) and emit
 * `tts-escape`; the keydown guard below does the same for a focused webview
 * when no native adapter is running. This module keeps that flag, and the
 * mirrored `stopKeyArmed` field other windows read, equal to "armed".
 */
import { invoke } from "@tauri-apps/api/core";
import { emit, listen } from "@tauri-apps/api/event";
import {
  isShortcutModifier,
  shortcutEventCode,
  type ShortcutBinding,
} from "@/lib/shortcut-registry";
import { getShortcutBinding, matchesShortcut } from "@/lib/shortcuts";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";
import {
  isContinuousVoiceActive,
  useContinuousVoice,
} from "./continuous-voice-store";
import { canSpeak } from "./tts-can-speak";
import {
  syncTtsPlaybackState,
  type TtsSpeakingState,
  useTtsPlaybackStore,
} from "./tts-playback-store";
import {
  isSpeechMutedForUserTurn,
  muteSpeechForUserTurn,
} from "./tts-user-speech-interruption";
import { getWindowRoleSync } from "./use-window-role";
import { useVoiceAttentionStore } from "./voice-attention-store";
import { trackVoiceWork } from "./voice-work-tracker";

const SPEECH_STOPPED_EVENT = "denker://tts-stopped";
/** How long one native push may go unanswered before the next is sent. */
const NATIVE_PUSH_TIMEOUT_MS = 1_000;

/** Queued speech still in flight, with the execution it belongs to. */
const speechWork = new Map<Promise<unknown>, string | undefined>();
const pendingSpeechMaps: PendingSpeechMap<unknown>[] = [];
let stopSpeech: (() => void) | null = null;
let playing = false;
/** The level last sent to the native flag; null when a claim may have cleared it. */
let nativeFlag: boolean | null = false;
/** Native pushes go out one at a time, so the last one sent is the one that holds. */
let nativePushes: Promise<unknown> = Promise.resolve();
let publishQueued = false;
/** The key a stop consumed; its auto-repeats and release are consumed too. */
let heldStopKey: string | null = null;

/** Replies about to be queued, by execution id. Size changes update armed. */
export class PendingSpeechMap<V> extends Map<string, V> {
  constructor() {
    super();
    pendingSpeechMaps.push(this);
  }
  set(executionId: string, value: V): this {
    super.set(executionId, value);
    refreshStopKey();
    return this;
  }
  delete(executionId: string): boolean {
    const removed = super.delete(executionId);
    if (removed) refreshStopKey();
    return removed;
  }
  clear(): void {
    super.clear();
    refreshStopKey();
  }
}

/** Execution ids (undefined for standalone speech) with speech still to come. */
function queuedSpeech(): Array<string | undefined> {
  return [
    ...speechWork.values(),
    ...pendingSpeechMaps.flatMap((pending) => [...pending.keys()]),
  ];
}

/** Whether this run is speaking or has speech still to come. */
export function hasSpeechFor(executionId: string): boolean {
  return queuedSpeech().includes(executionId);
}

/** The stop key keeps every run with speech still queued quiet for good. */
export function muteQueuedSpeech(): void {
  if (!canSpeak()) return;
  for (const executionId of queuedSpeech()) {
    if (executionId) muteSpeechForUserTurn({ executionId });
  }
}

/**
 * Playing, or queued for a run the user has not already silenced. Queues fill
 * whether or not read-aloud is on, so queued speech only counts when it can
 * actually be spoken.
 */
function isSpeechBusy(): boolean {
  return (
    playing ||
    (canSpeak() &&
      queuedSpeech().some(
        (executionId) =>
          !executionId || !isSpeechMutedForUserTurn({ executionId }),
      ))
  );
}

/** A voice request is being recorded or sent, outside live talk. Dictation
 *  anywhere counts too: its attention session disarms the stop key. */
function isDictating(): boolean {
  return (
    useVoiceAttentionStore.getState().recordingId !== null &&
    !isContinuousVoiceActive()
  );
}

function isStopKeyArmed(): boolean {
  return isSpeechBusy() && !isDictating();
}

/** Re-derive "armed" once the current burst of changes has settled. */
function refreshStopKey(): void {
  if (!stopSpeech || publishQueued) return;
  publishQueued = true;
  queueMicrotask(publishStopKey);
}

function publishStopKey(): void {
  publishQueued = false;
  const owner = getWindowRoleSync() === "owner";
  // A window that lost ownership while armed still has its flag to take down.
  const armed = owner && isStopKeyArmed();
  const playback = useTtsPlaybackStore.getState();
  if (owner && playback.stopKeyArmed !== armed) playback.setStopKeyArmed(armed);
  if (armed !== nativeFlag) pushNativeFlag(armed);
}

/**
 * Best effort: without the desktop shell the focused-webview guard remains.
 * Each push waits for the one before it, but never for longer than the
 * timeout, so a call the shell never answers cannot hold back later levels.
 */
function pushNativeFlag(active: boolean): void {
  nativeFlag = active;
  nativePushes = nativePushes.then(
    () =>
      new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, NATIVE_PUSH_TIMEOUT_MS);
        const done = () => {
          clearTimeout(timer);
          resolve();
        };
        void Promise.resolve()
          .then(() => invoke("set_tts_active", { active }))
          .then(done, done);
      }),
  );
}

/** Count queued speech as busy until it settles or a hard cancel drops it. */
export function trackSpeechWork<T>(
  work: Promise<T>,
  executionId?: string,
): Promise<T> {
  speechWork.set(work, executionId);
  refreshStopKey();
  const settled = () => {
    if (speechWork.delete(work)) refreshStopKey();
  };
  void work.then(settled, settled);
  return trackVoiceWork(work);
}

/** A hard cancel emptied every queue; what is still settling no longer counts. */
export function dropSpeechWork(): void {
  speechWork.clear();
  refreshStopKey();
}

/** Playback state: mirrored to the other windows by the owner, and to armed. */
export function reportSpeechPlayback(speakingState: TtsSpeakingState): void {
  playing = speakingState === "started";
  if (getWindowRoleSync() === "owner") syncTtsPlaybackState(speakingState);
  refreshStopKey();
}

/** The owner stopped speech: presentations in every other window end too. */
export function announceSpeechStopped(): void {
  void emit(SPEECH_STOPPED_EVENT, {});
}

/**
 * Whether the stop chord may be swallowed: Escape, a function key, or a key
 * pressed with a modifier. A bare printable key must keep typing and a
 * modifier-only chord has nothing to swallow, so those stop speech and still
 * reach their target. Same rule as `swallowable` in `shortcut_speech_stop.rs`.
 */
export function isSwallowableStopBinding(
  binding: Pick<ShortcutBinding, "keys">,
): boolean {
  return (
    binding.keys.some((key) => !isShortcutModifier(key)) &&
    binding.keys.some(
      (key) =>
        isShortcutModifier(key) || key === "Escape" || /^F\d+$/.test(key),
    )
  );
}

function consume(event: KeyboardEvent): void {
  event.preventDefault();
  event.stopImmediatePropagation();
}

function onKeyDown(event: KeyboardEvent): void {
  const code = shortcutEventCode(event);
  if (heldStopKey !== null) {
    if (code === heldStopKey && event.repeat) {
      consume(event);
      return;
    }
    // A fresh press of any key means the hold ended, seen or not.
    heldStopKey = null;
  }
  if (!matchesShortcut("shortcut.speech.stop", event)) return;
  const owner = getWindowRoleSync() === "owner";
  const playback = useTtsPlaybackStore.getState();
  if (!(owner ? isStopKeyArmed() : playback.stopKeyArmed)) return;
  if (isSwallowableStopBinding(getShortcutBinding("shortcut.speech.stop"))) {
    consume(event);
    heldStopKey = code;
  }
  // Like the native flag, a mirror's copy clears on use, so a stale `true`
  // costs one press at most.
  if (!owner) playback.setStopKeyArmed(false);
  stopSpeech?.();
}

function onKeyUp(event: KeyboardEvent): void {
  if (heldStopKey === null || shortcutEventCode(event) !== heldStopKey) return;
  heldStopKey = null;
  consume(event);
}

/**
 * Wire the stop key once per window. `stop` silences all speech and keeps the
 * replies it cut off quiet; on a mirror it relays to the owner.
 */
export function installSpeechStopKey(stop: () => void): void {
  stopSpeech = stop;
  // A reload must not leave the native flag set from the previous page.
  if (getWindowRoleSync() === "owner") pushNativeFlag(false);
  useTtsSettingsStore.subscribe(refreshStopKey);
  useContinuousVoice.subscribe((state, previous) => {
    if (state.enabled !== previous.enabled) refreshStopKey();
  });
  useVoiceAttentionStore.subscribe((state, previous) => {
    if (state.recordingId !== previous.recordingId) refreshStopKey();
  });
  // Deferred a task so every listener, the role's own included, has seen it.
  void listen("denker://owner-changed", () => setTimeout(refreshStopKey, 0));
  window.addEventListener("keydown", onKeyDown, { capture: true });
  window.addEventListener("keyup", onKeyUp, { capture: true });
  window.addEventListener("blur", () => {
    heldStopKey = null;
  });
  void listen<Record<string, never>>("tts-escape", () => {
    if (getWindowRoleSync() !== "owner") return;
    if (isStopKeyArmed()) stop();
    // A native claim clears its flag, so re-send whatever holds now.
    nativeFlag = null;
    refreshStopKey();
  });
  void listen<Record<string, never>>(SPEECH_STOPPED_EVENT, () => {
    if (getWindowRoleSync() === "owner") return;
    window.dispatchEvent(new Event("denker:presentation-stop"));
  });
}

export function resetSpeechStopKeyForTests(): void {
  speechWork.clear();
  playing = false;
  nativeFlag = false;
  heldStopKey = null;
  publishStopKey();
}
